'use client';

import { useSelectedSpecialtyCanonicalName } from '@/features/onboarding-v2/hooks/useSelectedSpecialtyCanonicalName';
import { useQuerySpecialtyOnboardingV2Assets } from '@/hooks/query/useQuerySpecialtyOnboardingV2Assets';
import { cn } from '@/lib/cn';
import { parseEvidenceResponseIntoLines, type EvidenceLine } from '@heidi/growth';
import { Copy, Loader2, Search, ThumbsDown, ThumbsUp } from 'lucide-react';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
const LAYOUT_EASE = [0.4, 0, 0.2, 1] as const;
const LINE_INTERVAL_MS = 900;
// How long the "Retrieving Evidence…" phase holds before the answer reveals.
const RETRIEVE_MS = 1500;

function StatusPill({ label }: { label: string }) {
  return (
    <m.div
      layoutId="evidence-response-pill"
      className="my-0.5 flex w-full items-center gap-2 rounded-xl bg-surface-accent px-3 py-2"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        layout: { duration: 0.7, ease: LAYOUT_EASE },
        opacity: { duration: 0.2 },
      }}
    >
      <Loader2 className="size-3.5 animate-spin text-accent-forest" />
      <span className="text-[13px] font-semibold text-accent-forest">{label}</span>
    </m.div>
  );
}

export function MockEvidenceResponse({
  visible,
  onComplete,
  queryIndex = 0,
}: {
  visible: boolean;
  onComplete: () => void;
  /** Which suggested question this response answers (drives the heading/intro). */
  queryIndex?: number;
}) {
  const intl = useIntl();
  const specialtyAssetName = useSelectedSpecialtyCanonicalName();
  const { data: assets } = useQuerySpecialtyOnboardingV2Assets(
    specialtyAssetName,
    intl.locale
  );

  // Real per-specialty question for the tapped index; the matching answer is
  // rendered below. Falls back to the first entry, then a generic question.
  const question =
    assets?.evidence_queries?.[queryIndex] ??
    assets?.evidence_queries?.[0] ??
    intl.formatMessage({
      defaultMessage: 'What’s the recommended workup for this presentation?',
      id: 'UnMAPYpjTV',
      description:
        'Generic fallback Evidence question when no per-specialty question exists',
    });

  // Generic, specialty-agnostic fallback used only when the per-specialty
  // `evidence_response` is missing — must not claim a cardiac/CSANZ topic.
  const fallbackLines = useMemo<EvidenceLine[]>(
    () => [
      {
        type: 'subheading',
        text: intl.formatMessage({
          defaultMessage: 'Key considerations',
          id: '+ScY2j+TpU',
          description: 'Heading of the generic fallback Evidence answer in the demo',
        }),
      },
      {
        type: 'bullet',
        text: intl.formatMessage({
          defaultMessage:
            'Confirm the working assessment with a focused history and examination.',
          id: '9j7Mv0tJU2',
          description: 'First bullet of the generic fallback Evidence answer in the demo',
        }),
      },
      {
        type: 'bullet',
        text: intl.formatMessage({
          defaultMessage:
            'Arrange first-line investigations and timely review for this presentation.',
          id: 'BFVuoF9mxJ',
          description:
            'Second bullet of the generic fallback Evidence answer in the demo',
        }),
      },
      {
        type: 'source',
        text: intl.formatMessage({
          defaultMessage: 'Illustrative guideline summary — tutorial example only.',
          id: 'omBH18kpei',
          description: 'Source line of the generic fallback Evidence answer in the demo',
        }),
      },
    ],
    [intl]
  );

  const evidenceResponse =
    assets?.evidence_responses?.[queryIndex] ?? assets?.evidence_responses?.[0];
  const evidenceLines = useMemo<EvidenceLine[]>(() => {
    if (!evidenceResponse) return fallbackLines;
    const parsed = parseEvidenceResponseIntoLines(evidenceResponse);
    return parsed.length > 0 ? parsed : fallbackLines;
  }, [evidenceResponse, fallbackLines]);

  // Three-phase reveal: 'retrieving' shows only the question + searched-sources
  // + loading pill; once retrieval completes the answer intro + cited lines
  // animate in ('answering'); 'done' enables the actions row.
  const [phase, setPhase] = useState<'retrieving' | 'answering' | 'done'>('retrieving');
  const [revealed, setRevealed] = useState(0);
  const lineCount = evidenceLines.length;
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Reset + replay the retrieval hold whenever the selected question changes
  // (and on mount). Driving this off `queryIndex` rather than a `key` remount
  // keeps a single persistent instance — no overlapping/duplicated content
  // during the swap, and tab visibility doesn't restart generation.
  useEffect(() => {
    setPhase('retrieving');
    setRevealed(0);
    const id = setTimeout(() => setPhase('answering'), RETRIEVE_MS);
    return () => clearTimeout(id);
  }, [queryIndex]);

  useEffect(() => {
    if (phase !== 'answering') return;
    if (lineCount === 0) {
      setPhase('done');
      onCompleteRef.current();
      return;
    }
    let count = 0;
    const id = setInterval(() => {
      count += 1;
      setRevealed(count);
      if (count >= lineCount) {
        clearInterval(id);
        setTimeout(() => {
          setPhase('done');
          setTimeout(() => onCompleteRef.current(), 800);
        }, 400);
      }
    }, LINE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [phase, lineCount]);

  return (
    <div className={cn('flex flex-col gap-2.5 px-5 py-4', !visible && 'hidden')}>
      <div className="flex items-start gap-2 text-[13px] font-semibold text-text-primary">
        <Search className="mt-0.5 size-3.5 shrink-0 text-accent-forest" />
        <span>{question}</span>
      </div>
      <div className="flex items-center gap-2 text-xs text-text-tertiary">
        <span>
          <FormattedMessage
            defaultMessage="{count, plural, one {Searched # source} other {Searched # sources}}"
            id="/W0o21BN7W"
            description="Count of sources the demo Evidence response searched"
            values={{ count: 3 }}
          />
        </span>
        <div className="flex items-center gap-1">
          <div className="size-2.5 rounded-full bg-accent-pink" />
          <div className="size-2.5 rounded-full bg-accent-forest" />
          <div className="size-2.5 rounded-full bg-accent-blue" />
        </div>
      </div>

      <AnimatePresence>
        {phase === 'retrieving' && (
          <StatusPill
            label={intl.formatMessage({
              defaultMessage: 'Retrieving Evidence',
              id: '7jJTaW8Ce3',
              description: 'Loading pill while the demo Evidence response is retrieved',
            })}
          />
        )}
      </AnimatePresence>

      {phase !== 'retrieving' && (
        <>
          <m.div
            className="flex flex-col gap-1.5"
            layout
            transition={{ layout: { duration: 0.7, ease: LAYOUT_EASE } }}
          >
            {evidenceLines.slice(0, revealed).map((line, i) => (
              <m.div
                key={i}
                layout
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  layout: { duration: 0.7, ease: LAYOUT_EASE },
                  opacity: { duration: 0.6, ease: 'easeOut' },
                }}
              >
                {line.type === 'subheading' && (
                  <p className="mt-1.5 text-[13px] font-semibold text-text-primary">
                    {line.text}
                  </p>
                )}
                {line.type === 'paragraph' && (
                  <p className="text-[13px] leading-snug text-text-secondary">
                    {line.text}
                  </p>
                )}
                {line.type === 'bullet' && (
                  <p className="text-[13px] leading-snug text-text-secondary">
                    &bull; {line.text}
                  </p>
                )}
                {line.type === 'source' && (
                  <div className="mt-1.5 flex items-start gap-2 rounded-lg border border-border bg-surface-accent/50 px-3 py-2">
                    <span className="mt-0.5 text-xs text-text-tertiary">📄</span>
                    <p className="text-[11px] italic leading-snug text-text-tertiary">
                      {line.text}
                    </p>
                  </div>
                )}
              </m.div>
            ))}
          </m.div>
        </>
      )}

      {phase === 'done' && (
        <m.div
          className="flex items-center justify-between border-t border-border pt-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              className="rounded-md p-1 text-text-tertiary hover:bg-surface-accent hover:text-text-secondary"
            >
              <ThumbsUp className="size-3.5" />
            </button>
            <button
              type="button"
              className="rounded-md p-1 text-text-tertiary hover:bg-surface-accent hover:text-text-secondary"
            >
              <ThumbsDown className="size-3.5" />
            </button>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              className="rounded-md p-1 text-text-tertiary hover:bg-surface-accent hover:text-text-secondary"
            >
              <Copy className="size-3.5" />
            </button>
            <button
              type="button"
              className="flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-[11px] text-text-secondary hover:bg-surface-accent"
            >
              <FormattedMessage
                defaultMessage="Actions"
                id="19GBUPHTYC"
                description="Button opening actions for the demo Evidence response"
              />
            </button>
          </div>
        </m.div>
      )}
    </div>
  );
}
